/**
 * Pending approvals (C19): `GET /approvals` (approvers see every item, others their own), approve /
 * reject with the approver's own PIN (D-035: approver ≠ maker, reason required on rejection), cancel by
 * the maker. Opened from an approvals notification, from the header chip (approvers with pending items)
 * and from the Audit & Control section. Amount = the server's IQD-equivalent measure (D-131 gross-moved).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApprovalsPanel, formatWithCommas, type ApprovalRow } from "@faylasuf/ui";
import type { PendingRow } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";

export function usePendingApprovals(enabled = true) {
  const { source } = useApp();
  const { sessionLost } = useSession();
  const q = useQuery({ queryKey: keys.approvals, queryFn: () => source.approvals(), enabled, staleTime: 30_000 });
  if (q.error && isSessionLost(q.error)) sessionLost();
  return q;
}

function describe(t: (k: string, p?: Record<string, string | number>) => string, r: PendingRow, names: ReadonlyMap<string, string>): string {
  const n = (id: unknown) => (typeof id === "string" ? (names.get(id) ?? id.slice(0, 8)) : "");
  const i = r.input;
  if (typeof i["fromAccountId"] === "string" && typeof i["toAccountId"] === "string") return t("approvals.desc_transfer", { from: n(i["fromAccountId"]), to: n(i["toAccountId"]) });
  if (typeof i["boxAccountId"] === "string") return t("approvals.desc_box", { box: n(i["boxAccountId"]) });
  if (typeof i["destinationAccountId"] === "string") return t("approvals.desc_box", { box: n(i["destinationAccountId"]) });
  if (typeof i["accountId"] === "string") return t("approvals.desc_box", { box: n(i["accountId"]) });
  return typeof i["note"] === "string" ? (i["note"] as string) : "";
}

export function ApprovalsContainer({ accountNames, onClose }: { accountNames: ReadonlyMap<string, string>; onClose: () => void }) {
  const { source, t, locale } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();
  const q = usePendingApprovals();
  const [busyId, setBusyId] = useState<string | null>(null);

  const done = () => {
    void qc.invalidateQueries({ queryKey: keys.approvals });
    void qc.invalidateQueries({ queryKey: keys.accounts });
    void qc.invalidateQueries({ queryKey: keys.dashboard });
    void qc.invalidateQueries({ queryKey: keys.notifications });
  };
  const onError = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };

  const approve = useMutation({
    mutationFn: async (id: string) => {
      setBusyId(id);
      const res = await pin.withPin((proof) => source.approve(id, crypto.randomUUID(), proof.pin ?? ""), { message: t("pin.message_approve") });
      return res ?? null;
    },
    onSuccess: (res) => {
      if (res) toast(t("toast.approved"), "success");
      done();
    },
    onError,
    onSettled: () => setBusyId(null),
  });
  const reject = useMutation({
    mutationFn: async (args: { id: string; reason: string }) => {
      setBusyId(args.id);
      return (await pin.withPin((proof) => source.reject(args.id, args.reason, proof.pin ?? ""), { message: t("pin.message_reject") })) ?? null;
    },
    onSuccess: (res) => {
      if (res) toast(t("toast.rejected"), "info");
      done();
    },
    onError,
    onSettled: () => setBusyId(null),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => source.cancelPending(id),
    onSuccess: () => {
      toast(t("toast.request_cancelled"), "info");
      done();
    },
    onError,
  });

  const canApprove = hasPermission("approve_operations");
  const rows: ApprovalRow[] = (q.data ?? []).map((r) => {
    const typeLabel = t(`optype.${r.type}`);
    return {
      id: r.id,
      typeLabel: typeLabel === `optype.${r.type}` ? r.type : typeLabel,
      amountLabel: `${formatWithCommas(r.iqd_equivalent)} ${t("unit.iqd")}`,
      maker: r.maker,
      createdLabel: new Date(r.created_at).toLocaleString(locale === "ar" ? "ar-IQ-u-nu-latn" : "en-US", { timeZone: session.office.timeZone, dateStyle: "short", timeStyle: "short" }),
      status: r.status,
      statusLabel: t(`approvals.status_${r.status}`),
      description: describe(t, r, accountNames),
      decidedBy: r.decided_by,
      reason: r.reason,
      canDecide: canApprove && r.maker !== session.user.username,
      canCancel: r.maker === session.user.username,
    };
  });

  return (
    <>
      <ApprovalsPanel rows={rows} loading={q.isLoading} busyId={busyId} onApprove={(id) => approve.mutate(id)} onReject={(id, reason) => reject.mutate({ id, reason })} onCancel={(id) => cancel.mutate(id)} onClose={onClose} />
      {pin.dialog}
    </>
  );
}
